import type { ChatMessage } from '../../../../channels/chat/components/sharedChatService';

/** One row from an `index` payload: phase title + labels that become the TOC. */
export type IndexPhaseRow = {
    phase: string;
    slots: string[];
};

/**
 * Normalize `message.value` from the wire (supports Phase/Slots or phase/slots).
 */
export function normalizeIndexPayload(value: unknown): IndexPhaseRow[] {
    if (!Array.isArray(value)) return [];
    return value
        .map((item: Record<string, unknown>) => ({
            phase: String(item?.Phase ?? item?.phase ?? '').trim(),
            slots: Array.isArray(item?.Slots)
                ? (item.Slots as unknown[]).map((s) => String(s))
                : Array.isArray(item?.slots)
                  ? (item.slots as unknown[]).map((s) => String(s))
                  : [],
        }))
        .filter((row) => row.phase.length > 0);
}

/**
 * Build one phase document: top-level title (h1) + numbered table of contents from slot labels.
 * No product-specific wording — driven only by `phaseTitle` and `tocSlotLabels`.
 */
export function buildPhaseDocumentMarkdown(phaseTitle: string, tocSlotLabels: string[]): string {
    const labels = tocSlotLabels.map((s) => String(s).trim()).filter(Boolean);
    const toc = labels.map((s, i) => `${i + 1}. ${s}`).join('\n');
    return `# ${phaseTitle.trim()}\n\n## Table of Contents\n\n${toc}\n`;
}

/**
 * Append prose under a slot subheader (`### slotTitle`). Creates the h3 if missing.
 * Further `doc_summary` chunks for the same slot append before the next `###` section.
 */
export function appendUnderSlot(doc: string, slotTitle: string, prose: string): string {
    const t = prose.trim();
    const slot = slotTitle.trim();
    if (!t || !slot) return doc;
    const heading = `### ${slot}`;
    const base = doc.trimEnd();
    if (!base.includes(heading)) {
        return `${base}\n\n${heading}\n\n${t}\n`;
    }
    const start = base.indexOf(heading) + heading.length;
    const rest = base.slice(start);
    const nextH3 = rest.search(/\n### /);
    const end = nextH3 === -1 ? base.length : start + nextH3;
    const before = base.slice(0, end).trimEnd();
    const after = nextH3 === -1 ? '' : base.slice(end);
    return `${before}\n\n${t}\n${after}`;
}

export interface PhaseDocumentsState {
    phases: Record<string, string>;
    /** Order from the latest `index` payload, then phases only seen in `doc_summary`. */
    phaseOrder: string[];
}

export type PhaseDocumentEvent =
    | { kind: 'index'; rows: IndexPhaseRow[] }
    | { kind: 'doc_summary'; phase: string; slot: string; text: string };

function sortByTime(a: ChatMessage, b: ChatMessage): number {
    const ta = a.timestamp instanceof Date ? a.timestamp.getTime() : new Date(a.timestamp as string).getTime();
    const tb = b.timestamp instanceof Date ? b.timestamp.getTime() : new Date(b.timestamp as string).getTime();
    return ta - tb;
}

/**
 * Map assistant discovery messages to ordered events (index then doc_summary in time order).
 */
export function chatMessagesToPhaseDocumentEvents(messages: ChatMessage[]): PhaseDocumentEvent[] {
    const ordered = [...messages].sort(sortByTime);
    const events: PhaseDocumentEvent[] = [];
    for (const m of ordered) {
        if (m.channel !== 'discovery' || m.type !== 'assistant') continue;
        const mt = m.msgType ?? m.discovery?.msg_type;
        if (mt === 'index' && m.discovery?.index_value?.length) {
            events.push({ kind: 'index', rows: m.discovery.index_value });
            continue;
        }
        if (mt === 'doc_summary' && m.discovery?.phase && m.discovery?.slot) {
            events.push({
                kind: 'doc_summary',
                phase: m.discovery.phase,
                slot: m.discovery.slot,
                text: m.content ?? '',
            });
        }
    }
    return events;
}

/**
 * Generic replay: `index` creates/replaces one markdown script per phase; `doc_summary` fills a slot section.
 */
export function replayPhaseDocumentEvents(events: PhaseDocumentEvent[]): PhaseDocumentsState {
    const phases: Record<string, string> = {};
    let phaseOrder: string[] = [];

    for (const ev of events) {
        if (ev.kind === 'index') {
            phaseOrder = ev.rows.map((r) => r.phase.trim()).filter(Boolean);
            for (const row of ev.rows) {
                const p = row.phase.trim();
                if (!p) continue;
                phases[p] = buildPhaseDocumentMarkdown(p, row.slots);
            }
            continue;
        }
        if (ev.kind === 'doc_summary') {
            const phase = ev.phase.trim();
            const slot = ev.slot.trim();
            const text = ev.text;
            if (!phase) continue;
            const prev = phases[phase] ?? `# ${phase}\n\n`;
            phases[phase] = appendUnderSlot(prev, slot, text);
            if (!phaseOrder.includes(phase)) {
                phaseOrder = [...phaseOrder, phase];
            }
        }
    }

    return { phases, phaseOrder };
}

export type PhaseDocumentsResult = PhaseDocumentsState;

/** Same as {@link replayPhaseDocumentEvents} after extracting events from chat messages. */
export function buildPhaseDocumentsFromMessages(messages: ChatMessage[]): PhaseDocumentsResult {
    return replayPhaseDocumentEvents(chatMessagesToPhaseDocumentEvents(messages));
}

/** True when discovery has produced real prose (not just an `index` / TOC skeleton). */
export function hasActiveDiscoveryDocumentContent(messages: ChatMessage[]): boolean {
    const events = chatMessagesToPhaseDocumentEvents(messages);
    for (const ev of events) {
        if (ev.kind === 'doc_summary' && ev.text.trim().length > 0) {
            return true;
        }
    }
    return false;
}

/**
 * Combine phase documents into one Markdown string (phase separators as `---`).
 */
export function joinPhaseDocumentsToMarkdown(state: PhaseDocumentsState): string {
    const { phases, phaseOrder } = state;
    const keys =
        phaseOrder.length > 0
            ? [...phaseOrder, ...Object.keys(phases).filter((k) => !phaseOrder.includes(k))]
            : Object.keys(phases);
    return keys.map((k) => phases[k]).filter(Boolean).join('\n\n---\n\n');
}

/** Download helper: triggers a browser save of a `.md` file. */
export function downloadMarkdownFile(filename: string, markdown: string): void {
    const blob = new Blob([markdown], { type: 'text/markdown;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename.endsWith('.md') ? filename : `${filename}.md`;
    a.click();
    URL.revokeObjectURL(url);
}
